/**
 * useGaushalas Hook
 * Story 2-4: Gaushala Selector Component (Frontend)
 *
 * React Query hook for fetching and caching gaushala data
 */

import { useQuery } from '@tanstack/react-query';
import { gaushalaApi, type GaushalaDTO } from '@/services/gaushala/gaushalaApi';
import type { GaushalaOption } from '@/components/manufacturing/selectors/GaushalaSelector';

/**
 * Transform GaushalaDTO to GaushalaOption format
 * Combines district and area for location display
 *
 * @param dto - GaushalaDTO from backend
 * @returns GaushalaOption for component use
 */
function transformToOption(dto: GaushalaDTO): GaushalaOption {
  // Build location string from available fields
  // Backend uses gaushalaName and registeredAddress instead of name and address
  const name = (dto as any).gaushalaName || dto.name || 'Unknown';
  const address = (dto as any).registeredAddress || dto.address || '';

  const locationParts: string[] = [];

  if (dto.district) locationParts.push(dto.district);
  if (dto.area) locationParts.push(dto.area);

  const location = locationParts.length > 0 ? locationParts.join(', ') : address || 'Unknown';

  return {
    id: dto.id,
    name,
    location,
    address,
    registrationNumber: dto.registrationNumber,
    district: dto.district,
    area: dto.area,
  };
}

/**
 * Fetch all gaushalas with caching and error handling
 *
 * Features:
 * - Data is cached for 5 minutes (staleTime)
 * - Automatic background refetch on window focus
 * - Error handling with retry logic
 * - Transforms backend DTOs to component-friendly format
 *
 * @returns React Query result with gaushala options
 *
 * @example
 * ```tsx
 * const { data: gaushalas, isLoading, isError, refetch } = useGaushalas();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (isError) return <div>Error loading gaushalas</div>;
 *
 * return (
 *   <ul>
 *     {gaushalas?.map(g => <li key={g.id}>{g.name}</li>)}
 *   </ul>
 * );
 * ```
 */
export function useGaushalas() {
  return useQuery<GaushalaOption[], Error>({
    queryKey: ['gaushalas'],
    queryFn: async () => {
      const gaushalas = await gaushalaApi.getGaushalas();
      return gaushalas.map(transformToOption);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 2,
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch a single gaushala by ID
 *
 * @param id - Gaushala ID
 * @param enabled - Whether to enable the query (default: true)
 * @returns React Query result with gaushala option
 *
 * @example
 * ```tsx
 * const { data: gaushala, isLoading } = useGaushala(1);
 * ```
 */
export function useGaushala(id: number | null, enabled: boolean = true) {
  return useQuery<GaushalaOption | null, Error>({
    queryKey: ['gaushala', id],
    queryFn: async () => {
      if (!id) return null;
      const gaushala = await gaushalaApi.getGaushalaById(id);
      return transformToOption(gaushala);
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && id !== null,
    retry: 2,
  });
}

/**
 * Fetch current user's gaushala
 *
 * @returns React Query result with current gaushala option
 *
 * @example
 * ```tsx
 * const { data: currentGaushala, isLoading } = useCurrentGaushala();
 * ```
 */
export function useCurrentGaushala() {
  return useQuery<GaushalaOption, Error>({
    queryKey: ['currentGaushala'],
    queryFn: async () => {
      const gaushala = await gaushalaApi.getCurrentGaushala();
      return transformToOption(gaushala);
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}
